import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Calendar, Repeat, ShieldCheck } from "lucide-react";
import { useTestRide } from "../components/TestRideContext";

const PLANS = [
  { id: "daily", name: "Daily", price: "₹199", per: "/day", use: "Try before you commit",
    features: ["Free pickup at store", "Helmet included", "Battery + charger", "Up to 50 km range"] },
  { id: "weekly", name: "Weekly", price: "₹999", per: "/week", use: "Short stays, trial weeks",
    features: ["Everything in Daily", "Battery support", "Free swap once a week", "Easy renewals"] },
  { id: "monthly", name: "Monthly", price: "₹2,499", per: "/month", badge: "Most popular",
    use: "Gig workers, daily commuters",
    features: ["Free monthly service", "24×7 helpline", "Insurance bundled", "Free swap twice/month"] },
  { id: "rto", name: "Rent-to-Own", price: "₹2,999", per: "/month", use: "Own your ride in 18 months",
    features: ["Convert rent to EMI", "Lifetime frame warranty", "Free annual servicing", "Transferable plan"] },
];

export default function Rent() {
  const { openModal } = useTestRide();
  return (
    <div data-testid="rent-page" className="bg-white">
      <section className="relative pt-16 pb-20 border-b border-slate-200 overflow-hidden bg-gradient-to-b from-[#F0F6FF] to-white">
        <div className="absolute inset-0 helix-bg" />
        <div className="container-x relative grid lg:grid-cols-2 gap-10 items-end">
          <div>
            <p className="eyebrow">Rental plans</p>
            <h1 className="font-outfit text-5xl lg:text-7xl font-bold mt-5 leading-[0.98] tracking-tighter text-slate-900">
              Flexible rental plans <br />
              <span className="text-slate-400">for every rider.</span>
            </h1>
          </div>
          <p className="text-slate-600 text-lg">
            Pay-as-you-ride. From single-day trials to full rent-to-own — every plan ships with service, battery support and zero hidden costs.
          </p>
        </div>
      </section>

      <section className="container-x py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: "Gig Workers", text: "Earn more with predictable monthly costs and free swaps.", icon: <Repeat size={20} /> },
            { title: "Students & Commuters", text: "Skip fuel queues and metro lines. Ride on your schedule.", icon: <Calendar size={20} /> },
            { title: "Business Fleets", text: "Bulk rentals with a single invoice and dedicated support.", icon: <ShieldCheck size={20} /> },
          ].map((p) => (
            <div key={p.title} className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#2A8BFF]/40 hover:shadow transition">
              <span className="w-10 h-10 rounded-full bg-[#2A8BFF]/10 text-[#1E6FE5] inline-flex items-center justify-center">{p.icon}</span>
              <h3 className="font-outfit font-bold text-xl mt-4 text-slate-900">{p.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x pb-20" id="plans">
        <p className="eyebrow">Plans</p>
        <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] max-w-2xl text-slate-900">
          Pick a plan that fits your ride.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12" data-testid="plans-grid">
          {PLANS.map((p, i) => {
            const featured = p.badge;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`plan-${p.id}`}
                className={`relative rounded-3xl p-8 flex flex-col transition-all ${
                  featured
                    ? "bg-gradient-to-br from-[#1E6FE5] to-[#2A8BFF] text-white shadow-[0_18px_50px_rgba(42,139,255,0.25)]"
                    : "bg-white border border-slate-200 hover:border-[#2A8BFF]/40 hover:shadow-lg"
                }`}
              >
                {featured && (
                  <p className="absolute -top-3 left-6 bg-[#15B47E] text-white text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full">
                    {featured}
                  </p>
                )}
                <p className={`text-[11px] uppercase tracking-[0.2em] font-bold ${featured ? "text-white/80" : "text-slate-500"}`}>{p.use}</p>
                <h3 className={`font-outfit text-3xl font-bold mt-2 ${featured ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                <div className="flex items-baseline gap-2 mt-4">
                  <span className={`font-outfit text-4xl font-bold ${featured ? "text-white" : "text-slate-900"}`}>{p.price}</span>
                  <span className={featured ? "text-white/80 text-sm" : "text-slate-500 text-sm"}>{p.per}</span>
                </div>
                <ul className={`mt-6 space-y-3 text-sm flex-1 ${featured ? "text-white/90" : "text-slate-700"}`}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className={`${featured ? "text-white" : "text-[#15B47E]"} mt-0.5 shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openModal({ model: p.name + " rental" })}
                  data-testid={`plan-${p.id}-cta`}
                  className={featured
                    ? "mt-6 inline-flex items-center justify-center gap-2 bg-white text-[#1E6FE5] font-bold text-sm uppercase tracking-wide px-6 py-3 rounded-full hover:bg-white/95 transition"
                    : "btn-outline mt-6"
                  }
                >
                  Select Plan
                </button>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#F6F8FB] border-y border-slate-200 py-20">
        <div className="container-x">
          <p className="eyebrow">How it works</p>
          <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
            Four steps to your e-cycle.
          </h2>
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {[
              { n: "01", t: "Pick a plan", d: "Choose daily, weekly, monthly or rent-to-own." },
              { n: "02", t: "Pick a model", d: "Reserve any of our six e-cycles in seconds." },
              { n: "03", t: "Walk in to a store", d: "Bring an ID. Test, learn, sign and pay." },
              { n: "04", t: "Ride", d: "Free service support, swap and renewals at any store." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white border border-slate-200 rounded-2xl p-6"
              >
                <p className="text-[#1E6FE5] font-outfit text-2xl font-bold">{s.n}</p>
                <h4 className="font-outfit font-bold text-lg mt-3 text-slate-900">{s.t}</h4>
                <p className="text-sm text-slate-600 mt-2">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="gig" className="container-x py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow">For gig workers</p>
            <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
              Ride more.{" "}
              <span className="bg-gradient-to-r from-[#15B47E] to-[#2A8BFF] bg-clip-text text-transparent">Earn more.</span>
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed max-w-lg">
              Built for food, grocery, hyperlocal delivery and last-mile work. Predictable monthly costs, free swaps and ride-to-own pathways.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <button onClick={() => openModal({ model: "Gig monthly" })} className="btn-primary" data-testid="gig-monthly-cta">
                Rent for Work
              </button>
              <a href="#plans" className="btn-outline">Compare Plans <ArrowUpRight size={16} /></a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Compare title="eBikeGo e-cycle" value="₹85" sub="per day" tone="mint" />
            <Compare title="Petrol commute" value="₹250" sub="per day" tone="muted" />
          </div>
        </div>
      </section>
    </div>
  );
}

function Compare({ title, value, sub, tone }) {
  return (
    <div className={`rounded-2xl p-6 ${tone === "mint" ? "bg-gradient-to-br from-[#3FE0A6]/15 to-[#3FE0A6]/5 border-2 border-[#3FE0A6]/40" : "bg-white border border-slate-200"}`}>
      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">{title}</p>
      <p className={`font-outfit text-5xl font-bold mt-3 ${tone === "mint" ? "text-[#15B47E]" : "text-slate-900"}`}>{value}</p>
      <p className="text-sm text-slate-500 mt-1">{sub}</p>
    </div>
  );
}
